import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageTitle } from "@/components/DashUI";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, AlertTriangle } from "lucide-react";

const money = (n) => "$" + (Math.round(n || 0)).toLocaleString();
const PAYERS = ["Private Pay", "Medicare", "Medicaid", "1915(i)", "Innovations Waiver", "TRICARE", "CHAMPVA", "CHIP", "Other Government Program"];
const MODELS = ["percentage_first_month", "fixed_placement_fee", "percentage_provider_fee", "fixed_acquisition_fee",
  "percentage_engagement", "percentage_respite_booking", "flat_business_development_fee", "no_commission"];

export default function CommissionOppsAdmin() {
  const [opps, setOpps] = useState([]);
  const [open, setOpen] = useState(false);
  const empty = { kind: "active", title: "", service_category: "Assisted Living", location: "", general_pricing: "",
    capacity: "", commission_model: "fixed_acquisition_fee", commission_value: 0, example_customer_value: 0,
    illustrative_commission: 0, requirements: "", terms: "", payer_type: "Private Pay", active: false, body: "" };
  const [f, setF] = useState(empty);
  const load = () => api.get("/admin/commission-opportunities").then((r) => setOpps(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target?.value ?? e });

  const toggleActive = async (o) => {
    await api.patch(`/admin/commission-opportunities/${o.id}`, { active: !o.active, payer_type: o.payer_type });
    load();
    toast.success(!o.active ? "Activation attempted" : "Deactivated");
  };
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/commission-opportunities", f); toast.success("Opportunity created"); setOpen(false); setF(empty); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div>
      <PageTitle title="Commission Opportunities" subtitle="Illustrative economics vs. admin-approved active opportunities">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-opp-btn" className="bg-navy hover:bg-navy/90"><Plus className="mr-2 h-4 w-4" /> New Opportunity</Button></DialogTrigger>
          <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading">Create opportunity</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4" data-testid="opp-form">
              <FF label="Title" req><Input data-testid="opp-title" required value={f.title} onChange={set("title")} /></FF>
              <div className="grid gap-4 sm:grid-cols-2">
                <FF label="Kind">
                  <Select value={f.kind} onValueChange={(v) => setF({ ...f, kind: v })}>
                    <SelectTrigger data-testid="opp-kind"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="illustrative">Illustrative</SelectItem><SelectItem value="active">Active</SelectItem></SelectContent>
                  </Select>
                </FF>
                <FF label="Service"><Input data-testid="opp-service" value={f.service_category} onChange={set("service_category")} /></FF>
                <FF label="Location"><Input data-testid="opp-location" value={f.location} onChange={set("location")} /></FF>
                <FF label="Payer type">
                  <Select value={f.payer_type} onValueChange={(v) => setF({ ...f, payer_type: v })}>
                    <SelectTrigger data-testid="opp-payer"><SelectValue /></SelectTrigger>
                    <SelectContent>{PAYERS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </FF>
                <FF label="Commission model">
                  <Select value={f.commission_model} onValueChange={(v) => setF({ ...f, commission_model: v })}>
                    <SelectTrigger data-testid="opp-model"><SelectValue /></SelectTrigger>
                    <SelectContent>{MODELS.map((m) => <SelectItem key={m} value={m}>{m.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                  </Select>
                </FF>
                <FF label="Illustrative commission ($)"><Input data-testid="opp-commission" type="number" value={f.illustrative_commission} onChange={(e) => setF({ ...f, illustrative_commission: Number(e.target.value) })} /></FF>
              </div>
              <FF label="Requirements"><Input data-testid="opp-requirements" value={f.requirements} onChange={set("requirements")} /></FF>
              <FF label="Terms / body"><Input data-testid="opp-body" value={f.body} onChange={set("body")} /></FF>
              <p className="rounded-lg bg-amberbrand/10 p-3 text-xs text-slate-600">Government-payer opportunities cannot be activated and are marked COMPLIANCE REVIEW REQUIRED automatically.</p>
              <Button type="submit" data-testid="opp-submit-btn" className="w-full bg-navy hover:bg-navy/90">Create</Button>
            </form>
          </DialogContent>
        </Dialog>
      </PageTitle>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {opps.map((o) => (
          <Card key={o.id} className="p-5" data-testid={`admin-opp-${o.id}`}>
            <div className="flex items-center justify-between">
              <Badge className={o.kind === "active" ? "bg-emeraldbrand/10 text-emeraldbrand hover:bg-emeraldbrand/10" : "bg-amberbrand/10 text-amberbrand hover:bg-amberbrand/10"}>
                {o.kind === "active" ? "ACTIVE" : "ILLUSTRATIVE"}
              </Badge>
              {o.kind === "active" && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-400">{o.active ? "Published" : "Draft"}</span>
                  <Switch data-testid={`opp-toggle-${o.id}`} checked={o.active} onCheckedChange={() => toggleActive(o)} />
                </div>
              )}
            </div>
            <p className="mt-3 font-heading font-semibold text-navy">{o.title}</p>
            <p className="text-xs uppercase tracking-wide text-slate-400">{o.service_category}{o.location ? ` · ${o.location}` : ""}</p>
            <p className="mt-2 text-sm font-semibold text-emeraldbrand">{money(o.illustrative_commission)}</p>
            {o.compliance_status && o.compliance_status !== "APPROVED" && (
              <p className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-amberbrand"><AlertTriangle className="h-3.5 w-3.5" /> {o.compliance_status}</p>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}
function FF({ label, req, children }) {
  return <div><Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}{req && " *"}</Label><div className="mt-1">{children}</div></div>;
}
